import React from 'react';
import { NavLink, Outlet, useNavigate, Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Users, Package, ClipboardList, Truck, Receipt, IndianRupee, BarChart3, Sparkles, LogOut, Menu, X, Tags, MapPinned, Settings, Archive } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import PWAInstallPrompt from '../../components/PWAInstallPrompt';
import LanguageSelect from '../../components/language/LanguageSelect';

const nav = [
  ['', LayoutDashboard, 'Dashboard'],
  ['customers', Users, 'Customers'],
  ['products', Package, 'Products'],
  ['categories', Tags, 'Categories'],
  ['inventory', Archive, 'Inventory'],
  ['routes', MapPinned, 'Routes'],
  ['subscriptions', ClipboardList, 'Subscriptions'],
  ['deliveries', Truck, 'Deliveries'],
  ['billing', Receipt, 'Billing'],
  ['payments', IndianRupee, 'Payments'],
  ['reports', BarChart3, 'Reports'],
  ['ai', Sparkles, 'AI Assistant'],
  ['settings', Settings, 'Settings'],
];

export default function MerchantLayout() {
  const { profile, business, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const storeName = business?.business_name || profile?.full_name || 'Store';
  const isAiPage = location.pathname === '/app/ai';
  const topbarTitle = isAiPage ? 'AI Assistant' : storeName;
  const topbarEyebrow = isAiPage ? 'Ask questions or give instructions in plain language.' : 'Business';

  const handleLogout = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const Item = ({ to, icon: Icon, label, compact = false }) => (
    <NavLink
      to={to === '' ? '/app' : `/app/${to}`}
      end={to === ''}
      onClick={() => setOpen(false)}
      data-testid={`merchant-nav-${to || 'dashboard'}`}
      title={compact ? label : undefined}
      className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${compact ? 'justify-center px-3' : ''} ${isActive ? 'bg-rozops-blue text-white shadow-soft' : 'text-rozops-text-muted hover:bg-rozops-blue-light hover:text-rozops-blue'}`}
    >
      <Icon className="w-4.5 h-4.5 shrink-0" strokeWidth={2} />
      {!compact && <span>{label}</span>}
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-rozops-bg">
      {/* Sidebar (desktop) */}
      <aside className={`hidden lg:flex fixed left-0 top-0 bottom-0 z-50 flex-col bg-white border-r border-gray-100 h-screen transition-all duration-200 ${sidebarCollapsed ? 'w-20' : 'w-64'}`}>
        <Link to="/app" className={`flex items-center h-16 border-b border-gray-100 ${sidebarCollapsed ? 'justify-center px-3' : 'px-5'}`}>
          <img src={sidebarCollapsed ? '/ro-icon.png' : '/ro-hlogo.png'} alt="RozOps" className={`${sidebarCollapsed ? 'h-10 w-10' : 'h-10 w-auto'} object-contain`} />
        </Link>
        <nav className="p-3 flex-1 space-y-1 overflow-y-auto">
          {nav.map(([t, i, l]) => <Item key={t} to={t} icon={i} label={l} compact={sidebarCollapsed} />)}
        </nav>
        <div className="p-3 border-t border-gray-100">
          {!sidebarCollapsed && (
            <div className="px-2 py-2 mb-2">
              <div className="text-xs text-rozops-text-muted">Signed in</div>
              <div className="text-sm font-semibold text-rozops-text truncate">{storeName}</div>
            </div>
          )}
          <button onClick={handleLogout} data-testid="merchant-logout" title="Log out" className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-rozops-text-muted hover:bg-gray-50 ${sidebarCollapsed ? 'justify-center' : ''}`}>
            <LogOut className="w-4 h-4" /> {!sidebarCollapsed && 'Log out'}
          </button>
        </div>
      </aside>

      <div className={`min-h-screen flex flex-col transition-all duration-200 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <div className="sticky top-0 z-40 bg-white border-b border-gray-100 h-14 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => {
                if (window.innerWidth >= 1024) setSidebarCollapsed(v => !v);
                else setOpen(v => !v);
              }}
              data-testid="merchant-sidebar-toggle"
              className="w-10 h-10 rounded-xl border border-gray-100 text-rozops-text-muted hover:text-rozops-blue hover:bg-rozops-blue-light grid place-items-center"
              aria-label="Toggle sidebar"
            >
              <span className="lg:hidden">{open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</span>
              <span className="hidden lg:block"><Menu className="w-5 h-5" /></span>
            </button>
            <div className="min-w-0">
              <div className={`${isAiPage ? 'font-heading font-bold text-rozops-navy truncate' : 'text-xs uppercase tracking-wider font-bold text-rozops-text-muted'}`}>{topbarTitle}</div>
              <div className={`${isAiPage ? 'text-xs text-rozops-text-muted truncate' : 'font-heading font-bold text-rozops-navy truncate'}`} data-testid="merchant-topbar-store-name">{topbarEyebrow}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSelect compact />
            <Link to="/app" className="lg:hidden flex items-center">
              <img src="/ro-icon.png" alt="RozOps" className="h-9 w-9 object-contain" />
            </Link>
          </div>
        </div>
        {open && (
          <div className="lg:hidden bg-white border-b border-gray-100 p-3 space-y-1">
            {nav.map(([t, i, l]) => <Item key={t} to={t} icon={i} label={l} />)}
            <button onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-rozops-text-muted"><LogOut className="w-4 h-4" /> Log out</button>
          </div>
        )}

        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="mb-4">
            <PWAInstallPrompt compact />
          </div>
          <Outlet />
        </main>
        {!isAiPage && (
          <Link
            to="/app/ai"
            data-testid="ai-mamu-fab"
            className="fixed bottom-5 right-5 z-50 group"
            aria-label="Open AI Mamu"
          >
            <span className="rounded-full bg-gradient-to-r from-rozops-accent to-rozops-blue text-white shadow-medium px-5 py-3 min-h-[56px] font-heading font-bold inline-flex items-center group-hover:shadow-xl group-hover:-translate-y-0.5 transition-all overflow-visible relative">
              <img src="/mamu.png" alt="" className="ai-mamu-jump absolute left-0 bottom-[16px] w-24 h-24 -translate-x-1/2 object-contain drop-shadow-lg pointer-events-none" />
              <span>AI Mamu</span>
            </span>
          </Link>
        )}
      </div>
    </div>
  );
}
